import React, { useState, useEffect } from 'react';
import { FiCopy } from 'react-icons/fi';
import api from '../services/api';
import './AttributieKoppeling.css';

const API_BASE = api.defaults.baseURL?.replace(/\/api\/?$/, '') || '';

export default function AttributieKoppeling() {
  const [trackingKey, setTrackingKey] = useState(null);
  const [snippetCopied, setSnippetCopied] = useState(false);
  const [shopifySnippetCopied, setShopifySnippetCopied] = useState(false);
  const [newKeyForCopy, setNewKeyForCopy] = useState(null);

  useEffect(() => {
    fetchTrackingKey();
  }, []);

  const fetchTrackingKey = async () => {
    try {
      const res = await api.get('/parameters/tracking_api_key').catch(() => ({ data: null }));
      setTrackingKey(res.data?.waarde ? '••••••••' + res.data.waarde.slice(-4) : null);
    } catch {
      setTrackingKey(null);
    }
  };

  const generateKey = async () => {
    const key = 'tk_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
    try {
      await api.post('/parameters', {
        parameter_naam: 'tracking_api_key',
        waarde: key,
        beschrijving: 'API-key voor eigen tracking (webshop-script)'
      });
      setTrackingKey('••••••••' + key.slice(-4));
      setNewKeyForCopy(key);
    } catch (e) {
      alert('Fout: ' + (e.response?.data?.error || e.message));
    }
  };

  const copyNewKey = () => {
    if (newKeyForCopy) {
      navigator.clipboard.writeText(newKeyForCopy);
    }
  };

  const snippet = `<!-- Inkoop Software - Eigen tracking -->
<script>
(function() {
  var API = "${API_BASE}/api";
  var KEY = "TRACKING_KEY";
  var vid = localStorage.getItem("inkoop_vid") || "v_" + Math.random().toString(36).slice(2) + Date.now();
  localStorage.setItem("inkoop_vid", vid);
  var params = new URLSearchParams(window.location.search);
  var payload = {
    visitor_id: vid,
    url: window.location.href,
    referrer: document.referrer || "",
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    landing_page: window.location.pathname || "/",
    shop_platform: "woo"
  };
  if (window.screen) payload.screen_width = window.screen.width;
  payload.device_type = payload.screen_width <= 768 ? "mobile" : "desktop";
  if (navigator && navigator.userAgent) payload.user_agent = navigator.userAgent;
  var xhr = new XMLHttpRequest();
  xhr.open("POST", API + "/tracking/bezoek");
  xhr.setRequestHeader("Content-Type", "application/json");
  xhr.setRequestHeader("X-Tracking-Key", KEY);
  xhr.send(JSON.stringify(payload));
  window.inkoopVisitorId = vid;
})();
</script>`;

  const shopifySnippet = `<!-- Inkoop Software – tracking (Shopify theme: bijv. theme.liquid vóór </body>) -->
<script>
(function() {
  var API = "${API_BASE}/api";
  var KEY = "TRACKING_KEY";
  var vid = localStorage.getItem("inkoop_vid_shopify") || "v_" + Math.random().toString(36).slice(2) + Date.now();
  localStorage.setItem("inkoop_vid_shopify", vid);
  var params = new URLSearchParams(window.location.search);
  var payload = {
    visitor_id: vid,
    url: window.location.href,
    referrer: document.referrer || "",
    utm_source: params.get("utm_source") || null,
    utm_medium: params.get("utm_medium") || null,
    utm_campaign: params.get("utm_campaign") || null,
    landing_page: window.location.pathname || "/",
    shop_platform: "shopify"
  };
  if (window.screen) payload.screen_width = window.screen.width;
  payload.device_type = payload.screen_width <= 768 ? "mobile" : "desktop";
  if (navigator && navigator.userAgent) payload.user_agent = navigator.userAgent;
  var xhr = new XMLHttpRequest();
  xhr.open("POST", API + "/tracking/bezoek");
  xhr.setRequestHeader("Content-Type", "application/json");
  xhr.setRequestHeader("X-Tracking-Key", KEY);
  xhr.send(JSON.stringify(payload));
  window.inkoopVisitorId = vid;
})();
</script>`;

  const orderSnippet = `<!-- Op de thank-you / order-received pagina: order koppelen aan visitor -->
<script>
if (window.inkoopVisitorId && typeof order_id !== "undefined") {
  var xhr = new XMLHttpRequest();
  xhr.open("POST", "${API_BASE}/api/tracking/order");
  xhr.setRequestHeader("Content-Type", "application/json");
  xhr.setRequestHeader("X-Tracking-Key", "TRACKING_KEY");
  xhr.send(JSON.stringify({ order_id: order_id, visitor_id: window.inkoopVisitorId }));
}
</script>`;

  const copySnippet = () => {
    navigator.clipboard.writeText(snippet);
    setSnippetCopied(true);
    setTimeout(() => setSnippetCopied(false), 2000);
  };

  const copyShopifySnippet = () => {
    navigator.clipboard.writeText(shopifySnippet);
    setShopifySnippetCopied(true);
    setTimeout(() => setShopifySnippetCopied(false), 2000);
  };

  return (
    <div className="attributie-koppeling">
      {newKeyForCopy && (
        <div className="key-modal-overlay" onClick={() => setNewKeyForCopy(null)}>
          <div className="key-modal" onClick={e => e.stopPropagation()}>
            <h3>Nieuwe key aangemaakt</h3>
            <p>Kopieer deze key en plak hem in de WordPress-plugin (Instellingen → Inkoop Tracking). Bewaar de key; hij wordt niet opnieuw getoond.</p>
            <div className="key-modal-key-wrap">
              <input
                type="text"
                readOnly
                value={newKeyForCopy}
                className="key-modal-input"
                onFocus={e => e.target.select()}
              />
              <button type="button" className="btn btn-primary" onClick={copyNewKey}>
                <FiCopy /> Kopieer key
              </button>
            </div>
            <p className="key-modal-hint">Je kunt de key ook selecteren en kopiëren (Ctrl+C).</p>
            <button type="button" className="btn btn-secondary" onClick={() => setNewKeyForCopy(null)}>Sluiten</button>
          </div>
        </div>
      )}

      {/* WordPress-plugin (aanbevolen) */}
      <div className="attributie-koppeling-section plugin-box">
        <h2 className="section-title">Makkelijkste: WordPress-plugin</h2>
        <p>
          Voor 4shine (WordPress/WooCommerce) kun je de <strong>Inkoop Tracking</strong>-plugin gebruiken. Geen snippets knippen/plakken.
        </p>
        <ol className="plugin-steps">
          <li>Genereer hieronder een <strong>tracking key</strong> (knop &quot;Genereer tracking key&quot;) als je die nog niet hebt.</li>
          <li>Download de plugin: in de repo staat <code>wordpress-plugin/inkoop-tracking</code>. Zip de map <code>inkoop-tracking</code> tot <code>inkoop-tracking.zip</code>.</li>
          <li>In WordPress: <strong>Plugins → Nieuwe plugin → Plugin uploaden</strong> → kies <code>inkoop-tracking.zip</code> → Installeren → Activeren.</li>
          <li><strong>Instellingen → Inkoop Tracking</strong>. Vul API basis-URL in (bijv. <code>https://inkoop-software-production.up.railway.app</code>) en plak de tracking key. Opslaan.</li>
        </ol>
        <p className="hint">Daarna worden bezoeken en order-koppeling automatisch doorgestuurd. Order-koppeling werkt op de WooCommerce bedankpagina. Het veld <code>shop_platform: &quot;woo&quot;</code> zorgt dat live bezoekers als Woo worden getoond.</p>
      </div>

      {/* Shopify Online Store */}
      <div className="attributie-koppeling-section shopify-box">
        <h2 className="section-title">Shopify storefront</h2>
        <p>
          Met dezelfde <strong>tracking key</strong> kun je pageviews van je <strong>Shopify</strong>-theme naar dezelfde API sturen. In het Attributie-dashboard verschijnen ze in <strong>Live bezoekers</strong> met het label <strong>Shopify</strong> (naast Woo-bezoeken).
        </p>
        <ol className="plugin-steps">
          <li>Zorg dat de tracking key in <strong>Parameters</strong> staat (of genereer hieronder een key).</li>
          <li>Kopieer de snippet onderaan en plak in <strong>Online Store → Themes → … → Edit code</strong>, bij voorkeur in <code>layout/theme.liquid</code> vlak voor <code>&lt;/body&gt;</code>.</li>
          <li>Vervang <code>TRACKING_KEY</code> door je echte key.</li>
          <li>We gebruiken een aparte localStorage-sleutel (<code>inkoop_vid_shopify</code>) zodat Woo- en Shopify-bezoekers niet per ongeluk dezelfde <code>visitor_id</code> delen.</li>
        </ol>
        <p className="hint">
          <strong>Order-koppeling thank-you:</strong> Shopify checkout gebruikt vaak een apart domein; koppel orders handmatig of via een volgende uitbreiding (Shopify pixel → backend). Alleen pageview-tracking werkt out-of-the-box zoals hieronder.
        </p>
        <div className="snippet-box">
          <pre>{shopifySnippet}</pre>
          <button type="button" className="btn btn-secondary" onClick={copyShopifySnippet}>
            <FiCopy /> {shopifySnippetCopied ? 'Gekopieerd!' : 'Kopieer Shopify-snippet'}
          </button>
        </div>
      </div>

      {/* Tracking key + snippet (handmatig) */}
      <div className="attributie-koppeling-section">
        <h2 className="section-title">Installatie (handmatig met snippets)</h2>
        {!trackingKey && (
          <p>
            <button type="button" className="btn btn-primary" onClick={generateKey}>
              Genereer tracking key
            </button>
            {' '}Maak een key aan en plaats daarna de snippet op je webshop.
          </p>
        )}
        {trackingKey && (
          <p>
            <strong>Tracking key:</strong> {trackingKey}
            {' '}
            <button type="button" className="btn btn-secondary" onClick={generateKey}>Nieuwe key</button>
          </p>
        )}
        <div className="snippet-box">
          <pre>{snippet}</pre>
          <button type="button" className="btn btn-secondary" onClick={copySnippet}>
            <FiCopy /> {snippetCopied ? 'Gekopieerd!' : 'Kopieer snippet'}
          </button>
        </div>
        <p className="hint">
          Vervang <code>TRACKING_KEY</code> door de gegenereerde key. Zet dit script in de footer van je webshop (bijv. WordPress theme of via een plugin). Voor Woo wordt <code>shop_platform: &quot;woo&quot;</code> meegestuurd.
        </p>
        <p className="hint">
          Op de <strong>bedankpagina</strong> na een bestelling: voeg de order-koppeling toe (zie code hieronder). Stel <code>order_id</code> in op het WooCommerce order-ID (bijv. uit <code>window</code> of je theme).
        </p>
        <div className="snippet-box">
          <pre>{orderSnippet}</pre>
        </div>
      </div>

      {/* Hoe controleren of het script geladen wordt */}
      <div className="attributie-koppeling-section check-section">
        <h2 className="section-title">Hoe zie ik of het script op de site geladen wordt?</h2>
        <ol className="check-steps">
          <li>
            <strong>Open je webshop</strong> in de browser (bijv. de homepage).
          </li>
          <li>
            <strong>Open Developer Tools:</strong> <kbd>F12</kbd> of rechtermuisklik → Inspecteren.
          </li>
          <li>
            <strong>Tab &quot;Network&quot; (Netwerk):</strong> Vink &quot;Preserve log&quot; aan. Vernieuw de pagina (<kbd>F5</kbd>).
          </li>
          <li>
            In de lijst zoek je een request naar <code>tracking/bezoek</code> of naar je API-domein (bijv. <code>railway.app</code>). Klik erop.
            <ul>
              <li><strong>Status 201</strong> – het script draait en het bezoek is opgeslagen. Je zou binnen enkele seconden in &quot;Live bezoekers&quot; moeten verschijnen.</li>
              <li><strong>Status 401</strong> – verkeerde of ontbrekende tracking key. Controleer de key in de plugin/snippet en in Parameters.</li>
              <li><strong>Status 503</strong> – tracking key niet geconfigureerd in de inkoopsoftware (Parameters, tracking_api_key).</li>
              <li><strong>Geen request naar tracking/bezoek</strong> – het script staat niet op de pagina of wordt geblokkeerd. Controleer of de plugin actief is of het snippet in de footer staat.</li>
            </ul>
          </li>
          <li>
            <strong>Optioneel – tab Console:</strong> Typ <code>window.inkoopVisitorId</code> en druk Enter. Als je een waarde ziet (bijv. <code>v_abc123...</code>), is het script geladen.
          </li>
        </ol>
      </div>
    </div>
  );
}
